import { useMemo, useState } from "react";
import clsx from "clsx";
import { heroApi } from "../api/hero";
import { useGameStore } from "../store/useGameStore";
import type { HeroClass, PassiveTreeEntry } from "../types";
import { SkillIcon } from "./SkillIcon";
import { formatExactNumber } from "../utils/formatNumber";
import { ModalBackdrop } from "./ModalBackdrop";

interface PassiveUpgradeModalProps {
  entry: PassiveTreeEntry;
  heroClass: HeroClass;
  heroLevel: number;
  skillPoints: number;
  gold: number;
  branch?: number;
  spCostPerUpgrade?: number;
  onClose: () => void;
  onUpgraded: () => void;
}

export function PassiveUpgradeModal({
  entry,
  heroClass,
  heroLevel,
  skillPoints,
  gold,
  branch = 0,
  spCostPerUpgrade = 1,
  onClose,
  onUpgraded,
}: PassiveUpgradeModalProps) {
  const { refreshHero, refreshBalance } = useGameStore();
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentLevel = entry.current_level;
  const isMax = currentLevel >= entry.max_level;
  const nextLevel = currentLevel + 1;
  const currentCfg = entry.levels[currentLevel - 1];
  const nextCfg = isMax ? null : entry.levels[nextLevel - 1];

  const canUpgrade = useMemo(() => {
    if (isMax || !nextCfg) return { ok: false, reason: "Max level reached" };
    if (!nextCfg.unlocked) return { ok: false, reason: nextCfg.reason ?? "Locked" };
    if (skillPoints < spCostPerUpgrade) return { ok: false, reason: "Not enough skill points" };
    if (gold < nextCfg.gold_cost) return { ok: false, reason: "Not enough gold" };
    return { ok: true, reason: null };
  }, [isMax, nextCfg, skillPoints, gold, spCostPerUpgrade]);

  const handleUpgrade = async () => {
    if (!canUpgrade.ok) return;
    setUpgrading(true);
    setError(null);
    try {
      await heroApi.upgradePassive(entry.passive_code, branch);
      await Promise.all([refreshHero(), refreshBalance()]);
      onUpgraded();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upgrade failed");
    } finally {
      setUpgrading(false);
    }
  };

  const description = nextCfg?.description ?? currentCfg?.description;

  return (
    <ModalBackdrop
      onClose={onClose}
      className="z-50 items-end md:items-center overflow-y-auto bg-black/60 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm max-h-[85vh] overflow-y-auto bg-kingdom-purple border border-kingdom-light rounded-2xl shadow-2xl md:my-auto">
        <div className="sticky top-0 z-10 bg-kingdom-purple border-b border-kingdom-mid/50 p-4 pb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <SkillIcon
              heroClass={heroClass}
              skill={entry.passive_code}
              className="w-12 h-12 shrink-0"
              alt={entry.display_name}
            />
            <div className="min-w-0">
              <div className="font-bold text-white truncate">{entry.display_name}</div>
              <div className="text-xs text-gray-400">Level {currentLevel}/{entry.max_level}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-white text-xl leading-none p-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-4 pt-3 space-y-4">

        {description && (
          <div className="bg-kingdom-dark rounded-xl p-3 text-sm text-gray-300 leading-relaxed">
            {description}
          </div>
        )}

        {!isMax && nextCfg ? (
          <>
            <div className="space-y-2 text-sm">
              <RequirementRow
                label="Hero level"
                met={heroLevel >= nextCfg.required_hero_level}
                value={`${heroLevel} / ${nextCfg.required_hero_level}`}
              />
              <RequirementRow
                label="Skill points"
                met={skillPoints >= spCostPerUpgrade}
                value={`${skillPoints} / ${spCostPerUpgrade}`}
              />
              <RequirementRow
                label="Gold"
                met={gold >= nextCfg.gold_cost}
                value={`${formatExactNumber(gold)} / ${formatExactNumber(nextCfg.gold_cost)}`}
              />
              {nextCfg.reason && !nextCfg.unlocked && (
                <div className="text-rose-400 text-xs bg-rose-900/20 border border-rose-500/30 rounded-lg p-2">
                  {nextCfg.reason}
                </div>
              )}
            </div>

            {error && (
              <div className="text-rose-400 text-xs bg-rose-900/20 border border-rose-500/30 rounded-lg p-2">
                {error}
              </div>
            )}

            <button
              onClick={handleUpgrade}
              disabled={!canUpgrade.ok || upgrading}
              className={clsx(
                "w-full py-3 rounded-xl font-bold transition-all",
                canUpgrade.ok && !upgrading
                  ? "bg-gradient-to-r from-kingdom-accent to-kingdom-glow text-white"
                  : "bg-gray-800 text-gray-500 cursor-not-allowed",
              )}
            >
              {upgrading
                ? "Upgrading..."
                : canUpgrade.ok
                  ? `Upgrade to Lv.${nextLevel}`
                  : canUpgrade.reason}
            </button>
          </>
        ) : (
          <div className="text-center text-gold-300 font-bold py-4">Max level reached!</div>
        )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function RequirementRow({
  label,
  met,
  value,
}: {
  label: string;
  met: boolean;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between bg-kingdom-dark rounded-lg px-3 py-2">
      <span className="text-gray-400">{label}</span>
      <span className={clsx("font-medium tabular-nums", met ? "text-emerald-400" : "text-rose-400")}>
        {met ? "✓" : "✗"} {value}
      </span>
    </div>
  );
}
